import { useEffect, useMemo, useState } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  // ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../../core/providers/AuthProvider';
import { useStudents } from '../hooks/useStudents';
import { ROLES } from '../../../shared/constant/roles';
import { GRADE_LEVELS } from '../../../shared/constant/gradeLevels';
import { compareByGradeOrder } from '../../../shared/utils/sortByGrade';
import { confirmAction, showAlert } from '../../../shared/utils/dialogs';
import Pagination from '../../../shared/components/Pagination';
import StudentFormModal from './StudentFormModal';
import StudentImportModal from './StudentImportModal';
// import { downloadStudentTemplate } from '../utils/studentTemplate';

const PAGE_SIZE = 10;

function StudentsPage() {
  const { role } = useAuth();
  const canDelete = role === ROLES.ADMIN || role === ROLES.SUPER_ADMIN;
  const { students, loading, error, create, bulkCreate, update, remove } = useStudents();
  const [modalState, setModalState] = useState(null); // null | 'new' | student object
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [schoolYearFilter, setSchoolYearFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const sectionOptions = useMemo(() => {
    const source = gradeFilter ? students.filter((s) => s.grade_level === gradeFilter) : students;
    return [...new Set(source.map((s) => s.section).filter(Boolean))].sort(compareByGradeOrder);
  }, [students, gradeFilter]);

  useEffect(() => {
    if (sectionFilter && !sectionOptions.includes(sectionFilter)) {
      setSectionFilter('');
    }
  }, [sectionOptions, sectionFilter]);

  const schoolYearOptions = useMemo(
    () => [...new Set(students.map((s) => s.school_year).filter(Boolean))].sort().reverse(),
    [students]
  );

  const filteredStudents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return students.filter((s) => {
      if (gradeFilter && s.grade_level !== gradeFilter) return false;
      if (sectionFilter && s.section !== sectionFilter) return false;
      if (schoolYearFilter && s.school_year !== schoolYearFilter) return false;
      if (statusFilter && s.status !== statusFilter) return false;
      if (!q) return true;
      return [s.firstname, s.lastname, s.lrn, s.guardian_name, s.grade_level, s.section]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [students, searchTerm, gradeFilter, sectionFilter, schoolYearFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedStudents = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredStudents.slice(start, start + PAGE_SIZE);
  }, [filteredStudents, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, gradeFilter, sectionFilter, schoolYearFilter, statusFilter]);

  const hasActiveFilters = Boolean(
    searchTerm || gradeFilter || sectionFilter || schoolYearFilter || statusFilter
  );

  const clearFilters = () => {
    setSearchTerm('');
    setGradeFilter('');
    setSectionFilter('');
    setSchoolYearFilter('');
    setStatusFilter('');
  };

  const handleDelete = async (student) => {
    const confirmed = await confirmAction({
      title: 'Delete student?',
      text: `Delete ${student.firstname} ${student.lastname}? This cannot be undone.`,
      confirmText: 'Delete',
      danger: true,
    });
    if (!confirmed) return;
    try {
      await remove(student.id, student);
    } catch (err) {
      showAlert({ title: 'Failed to delete student', text: err.message || 'Failed to delete student' });
    }
  };

  const handleSubmit = (formData) => {
    if (modalState && modalState !== 'new') {
      return update(modalState.id, formData, modalState);
    }
    return create(formData);
  };

  return (
    <section className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 sm:px-5 py-4 bg-navy-light space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-white">Students</h2>
            <p className="text-sm text-gray-200">Enrolled students</p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setModalState('new')}
              className="flex flex-1 sm:flex-none items-center justify-center gap-1.5 rounded-md bg-gold text-navy text-lg font-medium px-3 py-2 hover:bg-gold-dark"
            >
              <PlusIcon className="h-4 w-4" />
              Add Student
            </button>
            <button
              type="button"
              onClick={() => setIsImportOpen(true)}
              className="flex flex-1 sm:flex-none items-center justify-center gap-1.5 rounded-md bg-white text-navy text-lg font-medium px-3 py-2 hover:bg-gray-100"
            >
              <ArrowUpTrayIcon className="h-4 w-4" />
              Import
            </button>
            {/* <button
              type="button"
              onClick={downloadStudentTemplate}
              className="w-full flex items-center justify-center gap-1.5 rounded-md bg-white text-[#122436] text-sm font-medium px-3 py-2 hover:bg-gray-100"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Download Template
            </button> */}
          </div>
        </div>

        <div className="flex items-center gap-2 w-full">
          <div className="relative flex-1 min-w-0">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, LRN, grade, guardian…"
              className="w-full rounded-md border-0 pl-9 pr-3 py-2 text-lg focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowFilters((prev) => !prev)}
            aria-label="Toggle filters"
            aria-pressed={showFilters}
            className={`shrink-0 p-2.5 rounded-md border transition ${
              showFilters
                ? 'bg-white text-navy border-white'
                : 'border-gray-300 text-gray-200 hover:text-white hover:border-white'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
          </button>
        </div>

        {showFilters && (
          <div className="flex flex-wrap items-center gap-2 w-full">
            <select
              value={gradeFilter}
              onChange={(e) => setGradeFilter(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All Grades</option>
              {GRADE_LEVELS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>

            <select
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All Sections</option>
              {sectionOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>

            <select
              value={schoolYearFilter}
              onChange={(e) => setSchoolYearFilter(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border-0 px-3 py-2 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All School Years</option>
              {schoolYearOptions.map((sy) => (
                <option key={sy} value={sy}>
                  {sy}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              className="w-full sm:w-auto text-lg text-gray-200 border border-gray-300 px-3 py-2 rounded-md hover:text-white whitespace-nowrap disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed disabled:hover:text-gray-200"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {!loading && !error && filteredStudents.length > 0 && (
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          totalItems={filteredStudents.length}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}

      {loading ? (
        <p className="px-5 py-8 text-sm text-gray-500 text-center">Loading students…</p>
      ) : error ? (
        <p className="px-5 py-8 text-sm text-danger text-center">{error}</p>
      ) : filteredStudents.length === 0 ? (
        <p className="px-5 py-8 text-sm text-gray-500 text-center">
          {students.length === 0 ? 'No students found' : 'No students match your search/filters'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-200 text-md text-gray-500 uppercase">
                <th className="py-2 pl-5 pr-4 font-semibold whitespace-nowrap">Name</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">LRN</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Grade</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Section</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Guardian</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Status</th>
                <th className="py-2 pr-5 font-semibold whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedStudents.map((student) => (
                <tr key={student.id} className="hover:bg-gold/10">
                  <td className="py-3 pl-5 pr-4 font-medium text-gray-900 whitespace-nowrap">
                    {student.lastname}, {student.firstname}
                  </td>
                  <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{student.lrn || '—'}</td>
                  <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{student.grade_level}</td>
                  <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{student.section || '—'}</td>
                  <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{student.guardian_name || '—'}</td>
                  <td className="py-3 pr-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        student.status === 'Active' ? 'bg-success/10 text-success' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {student.status}
                    </span>
                  </td>
                  <td className="py-3 pr-5 whitespace-nowrap">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setModalState(student)}
                        className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100"
                        aria-label="Edit student"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDelete(student)}
                          className="p-1.5 rounded-md text-danger hover:bg-danger/10"
                          aria-label="Delete student"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalState && (
        <StudentFormModal
          student={modalState === 'new' ? null : modalState}
          onClose={() => setModalState(null)}
          onSubmit={handleSubmit}
        />
      )}

      {isImportOpen && (
        <StudentImportModal
          onClose={() => setIsImportOpen(false)}
          onImport={bulkCreate}
          existingLrns={students.map((s) => s.lrn)}
        />
      )}
    </section>
  );
}

export default StudentsPage;
